/**
 * The selection bar (1.2.0), shown above the results while two or more assets are selected: "12 selected", Clear,
 * and the actions on the selection. Each counts only the assets it can act on ("Run QC on 9 of 12") and is disabled
 * when none can or more than BULK_ACTION_LIMIT would. Export and Run workflow open the batch dialogs (stream X) with
 * the selected assets; Run QC, Send to edit and Restore use the existing dialogs and routes.
 */
import { useState } from 'react';
import { Archive, BadgeCheck, Clapperboard, FolderHeart, Share, Workflow, X } from 'lucide-react';
import { BULK_ACTION_LIMIT, type AssetSummary } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useMe } from '../../api/hooks';
import { useBulkRestore, useCanRestore } from '../../components/ArchiveRestore';
import { AddAssetsToCollectionDialog } from '../../components/AddToCollection';
import { BatchExportDialog, BatchRunWorkflowDialog } from '../../components/BatchActions';
import { RunQcDialog } from '../../components/QualityControl';
import { SendToEditDialog } from '../../components/SendToEdit';
import { toast } from '../../components/Toasts';
import { selectionAction, type SelectionActionKind } from './useLibrarySelection';
import { AssignForDeliverySelectionButton } from '../assignments/AssignForDelivery';
import { AddToGroupSelectionButton } from '../groups/AddToGroup';
import { DetectShotsSelectionButton } from '../../components/DetectShotsDialog';
import { RegenerateProxySelectionButton } from '../../components/RegenerateProxy';

type Open = 'export' | 'workflow' | 'qc' | 'edit' | 'collection' | null;

function ActionButton({ kind, verb, icon, selected, onClick, busy = false }: {
  kind: SelectionActionKind; verb: string; icon: React.ReactNode; selected: AssetSummary[]; onClick: (eligible: AssetSummary[]) => void; busy?: boolean;
}) {
  const a = selectionAction(kind, verb, selected);
  return (
    <button className="btn ghost sm" disabled={busy || Boolean(a.blocked)} title={a.title} onClick={() => onClick(a.eligible)}
      data-testid={`selection-${kind}`} data-eligible={a.eligible.length}>
      {icon} {a.label}
    </button>
  );
}

export function LibrarySelectionBar({ selected, onClear }: { selected: AssetSummary[]; onClear: () => void }) {
  const me = useMe().data;
  const perms = me?.permissions ?? [];
  const canRestore = useCanRestore();
  const bulkRestore = useBulkRestore();
  const [open, setOpen] = useState<Open>(null);
  const [eligible, setEligible] = useState<AssetSummary[]>([]);
  const show = (which: Open) => (list: AssetSummary[]) => { setEligible(list); setOpen(which); };
  // The batch dialogs get the whole selection while it is within the limit (they say which they skip and why);
  // above it, only the eligible ones (at most BULK_ACTION_LIMIT, or the button is disabled).
  const batch = (which: 'export' | 'workflow') => (list: AssetSummary[]) => { setEligible(selected.length <= BULK_ACTION_LIMIT ? selected : list); setOpen(which); };
  const close = () => setOpen(null);
  const n = selected.length;
  return (
    <div className="selection-bar" role="toolbar" aria-label="Selected assets" data-testid="selection-bar">
      <span className="selection-count" data-testid="selection-count">{n} selected</span>
      <button className="btn ghost sm" onClick={onClear} data-testid="selection-clear" title="Clear the selection (Esc)"><X size={13} /> Clear</button>
      <div className="selection-actions">
        {perms.includes('export.create') && (
          <ActionButton kind="export" verb="Export {n}…" icon={<Share size={13} />} selected={selected} onClick={batch('export')} />
        )}
        {perms.includes('automation.run') && (
          <ActionButton kind="workflow" verb="Run workflow on {n}…" icon={<Workflow size={13} />} selected={selected} onClick={batch('workflow')} />
        )}
        {perms.includes('asset.qc') && <ActionButton kind="qc" verb="Run QC on {n}…" icon={<BadgeCheck size={13} />} selected={selected} onClick={show('qc')} />}
        {/* 1.5.0 (S2): Detect shots on the selection, with the workspace's settings (processing.retry). */}
        <DetectShotsSelectionButton selected={selected} />
        <RegenerateProxySelectionButton selected={selected} />
        {perms.includes('export.create') && perms.includes('asset.download') && (
          <ActionButton kind="edit" verb="Send {n} to edit…" icon={<Clapperboard size={13} />} selected={selected} onClick={show('edit')} />
        )}
        {canRestore && (
          <ActionButton kind="restore" verb="Restore {n}" icon={<Archive size={13} />} selected={selected} busy={bulkRestore.isPending} onClick={(list) => {
            if (!window.confirm(`Restore the originals of ${list.length} archived asset${list.length === 1 ? '' : 's'}${list.length < n ? ` (${n - list.length} not archived are skipped)` : ''}? The archive may charge for retrievals.`)) return;
            bulkRestore.mutate(list.map((a) => a.id), {
              onSuccess: (r) => toast(`Restores requested: ${r.queued}${r.alreadyRestoring ? `, ${r.alreadyRestoring} already restoring` : ''}${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}`),
              onError: (e) => toast(errorMessage(e), 'error'),
            });
          }} />
        )}
        <ActionButton kind="collection" verb="Add {n} to collection…" icon={<FolderHeart size={13} />} selected={selected} onClick={show('collection')} />
        <AssignForDeliverySelectionButton selected={selected} />
        {/* 1.4.0 (G2): Add to group… (group.manage). */}
        <AddToGroupSelectionButton selected={selected} />
      </div>
      {open === 'export' && <BatchExportDialog selectedAssets={eligible} onClose={close} />}
      {open === 'workflow' && <BatchRunWorkflowDialog selectedAssets={eligible} onClose={close} />}
      {open === 'qc' && <RunQcDialog assetIds={eligible.map((a) => a.id)} name="the selection" skippedBefore={n - eligible.length} onClose={close} />}
      {open === 'edit' && <SendToEditDialog subject={{ kind: 'clips', assetIds: eligible.map((a) => a.id), name: 'Library selects' }} onClose={close} />}
      {open === 'collection' && <AddAssetsToCollectionDialog assetIds={eligible.map((a) => a.id)} onClose={close} />}
    </div>
  );
}
